import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { partners } from '../data/partners';

// Import des images
import activity1 from '../assets/images/activity1.jpg';
import activity2 from '../assets/images/activity2.jpg';
import activity3 from '../assets/images/activity3.jpg';
import partner1 from '../assets/images/partner1.jpg';

// Mapping des chemins d'images
const imageMap: {[key: string]: string} = {
  '/src/assets/images/partner1.jpg': partner1,
  '/src/assets/images/activity1.jpg': activity1,
  '/src/assets/images/activity2.jpg': activity2,
  '/src/assets/images/activity3.jpg': activity3
};

const Partners: React.FC = () => {
  const [selectedPartner, setSelectedPartner] = useState<number | null>(null);
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section 
      id="partners" 
      className="py-20 bg-gradient-to-b from-[#F5F5F5] to-white" 
      ref={ref}
    >
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-4">
            Nos <span className="text-[#FFD700]">partenaires</span> locaux
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Découvrez les structures locales qui s'associent à nous pour offrir des expériences enrichissantes aux enfants de notre communauté.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {partners.map((partner) => (
            <motion.div 
              key={partner.id}
              className={`bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 ${selectedPartner === partner.id ? 'lg:col-span-2' : ''}`}
              variants={itemVariants}
              whileHover={{ 
                y: -5,
                transition: { duration: 0.3 }
              }}
              onClick={() => setSelectedPartner(selectedPartner === partner.id ? null : partner.id)}
              layout
            >
              <div className={`flex flex-col md:flex-row ${selectedPartner === partner.id ? 'md:h-96' : 'md:h-64'}`}>
                <motion.div 
                  className={`md:w-1/2 ${selectedPartner === partner.id ? 'h-64 md:h-auto' : 'h-48 md:h-auto'}`}
                  layout
                >
                  <img 
                    src={imageMap[partner.image]} 
                    alt={partner.name} 
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                <motion.div 
                  className="md:w-1/2 p-6 flex flex-col justify-between"
                  layout
                >
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-bold text-[#1C1C1C]">{partner.name}</h3>
                      <span className="bg-[#FFD700] text-[#1C1C1C] text-xs font-semibold px-2.5 py-1 rounded-full">
                        {partner.type}
                      </span>
                    </div>
                    <p className="text-gray-600">
                      {selectedPartner === partner.id 
                        ? partner.description 
                        : `${partner.description.substring(0, 100)}${partner.description.length > 100 ? '...' : ''}`}
                    </p>
                  </div>
                  
                  <div className="mt-4 flex justify-between items-center">
                    <motion.a 
                      href={partner.website}
                      className="text-[#87CEFA] hover:text-[#66B2FF] font-medium"
                      whileHover={{ scale: 1.05 }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Visiter le site
                    </motion.a>
                    <motion.button
                      className="text-gray-500 hover:text-[#1C1C1C] transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {selectedPartner === partner.id ? 'Voir moins' : 'En savoir plus'}
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="text-center bg-[#87CEFA] bg-opacity-10 rounded-xl p-8 md:p-12"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h3 className="text-2xl font-bold text-[#1C1C1C] mb-4">Devenez partenaire</h3>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            Vous proposez des activités pour enfants ? Rejoignez notre réseau et faites découvrir vos offres à notre communauté de familles.
          </p>
          <motion.button 
            className="bg-[#FFD700] hover:bg-[#F8C400] text-[#1C1C1C] font-bold py-3 px-8 rounded-lg shadow-md transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Nous contacter
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Partners;
